// SCR-STAFF-102 تب خانواده — حساب خانوادگی (AP-PAT-03، US-PAT-042؛ BR-PAT-053 — C-AGENT-1349): هر مراجعه‌کننده حداکثر عضو یک حساب
// (عضو حساب دیگر PHX-PAT-0505)؛ ایجاد با سرپرست و اعضا، افزودن و حذف عضو، تعیین سرپرست و انحلال با pat.patient.update.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type FamilyAccountView, type FamilyMemberView, type PatientSearchItem, type PatientView } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ConfirmDialog, DataTable, ErrorState, faDigits, FormDialog, toJalali, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { patientPath, patKeys } from './common';
import { PatientPicker } from './PatientPicker';

function CreateFamilyDialog({ patient, open, onClose }: { patient: PatientView; open: boolean; onClose: () => void }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [name, setName] = useState(`${t('pat:family_account.default_name')} ${patient.lastName}`);
  const [members, setMembers] = useState<PatientSearchItem[]>([]);
  const [picker, setPicker] = useState<PatientSearchItem | null>(null);
  const create = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/patients/family-accounts', {
          headers: { 'Idempotency-Key': key.current() },
          body: { name: name.trim(), headPatientId: patient.id, memberIds: members.map((member) => member.id) },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (family) => {
      for (const member of family.members) {
        void queryClient.invalidateQueries({ queryKey: patKeys.familyAccount(session?.tenantId, member.patientId) });
      }
      setMembers([]);
      onClose();
    },
  });
  return (
    <FormDialog
      open={open}
      titleKey="pat:family_account.create"
      onSubmit={() => name.trim() && create.mutate()}
      onClose={onClose}
      isDirty={members.length > 0}
      isSubmitting={create.isPending}
      submitError={create.error}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField
          label={t('pat:family_account.name')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <Typography variant="body2">{t('pat:family_account.head_is', { name: patient.displayName })}</Typography>
        <PatientPicker
          labelKey="pat:family_account.member_picker"
          value={picker}
          onChange={(item) => {
            if (item && !members.some((member) => member.id === item.id)) {
              setMembers((list) => [...list, item]);
            }
            setPicker(null);
          }}
          excludeIds={[patient.id, ...members.map((member) => member.id)]}
          testId="family-new-member"
        />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {members.map((member) => (
            <Chip key={member.id} label={member.displayName} onDelete={() => setMembers((list) => list.filter((item) => item.id !== member.id))} />
          ))}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {t('pat:family_account.rule')}
        </Typography>
      </Stack>
    </FormDialog>
  );
}

export function FamilyAccountSection({ patient, canWrite }: { patient: PatientView; canWrite: boolean }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [dissolving, setDissolving] = useState(false);
  const [picker, setPicker] = useState<PatientSearchItem | null>(null);
  const family = useQuery({
    queryKey: patKeys.familyAccount(session?.tenantId, patient.id),
    queryFn: async (): Promise<FamilyAccountView | null> => {
      const response = await tenantApi.GET('/api/v1/patients/patients/{id}/family-account', { params: { path: { id: patient.id } } });
      if (response.response.status === 204) {
        return null;
      }
      return (await unwrap(Promise.resolve(response))) ?? null;
    },
  });
  const refresh = (view?: FamilyAccountView | null) => {
    const ids = new Set([patient.id, ...(family.data?.members ?? []).map((member) => member.patientId), ...(view?.members ?? []).map((member) => member.patientId)]);
    for (const id of ids) {
      void queryClient.invalidateQueries({ queryKey: patKeys.familyAccount(session?.tenantId, id) });
    }
  };
  const familyId = family.data?.id ?? '';
  const add = useMutation({
    mutationFn: (patientId: string) =>
      unwrap(tenantApi.POST('/api/v1/patients/family-accounts/{familyId}/members', { params: { path: { familyId } }, body: { patientId } })),
    onSuccess: (view) => {
      setPicker(null);
      refresh(view);
    },
  });
  const remove = useMutation({
    mutationFn: (patientId: string) =>
      unwrap(tenantApi.DELETE('/api/v1/patients/family-accounts/{familyId}/members/{patientId}', { params: { path: { familyId, patientId } } })),
    onSuccess: () => refresh(),
  });
  const setHead = useMutation({
    mutationFn: (headPatientId: string) =>
      unwrap(
        tenantApi.PUT('/api/v1/patients/family-accounts/{familyId}', {
          params: { path: { familyId } },
          body: { name: family.data?.name ?? null, headPatientId },
        }),
      ),
    onSuccess: (view) => refresh(view),
  });
  const dissolve = useMutation({
    mutationFn: async () => {
      const response = await tenantApi.DELETE('/api/v1/patients/family-accounts/{familyId}', { params: { path: { familyId } } });
      if (!response.response.ok) {
        await unwrap(Promise.resolve(response));
      }
    },
    onSuccess: () => {
      setDissolving(false);
      refresh();
    },
  });
  const error = add.error ?? remove.error ?? setHead.error ?? dissolve.error;
  const columns: DataTableColumn<FamilyMemberView>[] = [
    {
      id: 'member',
      headerKey: 'pat:family_account.members',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          {row.patientId === patient.id ? <span>{row.displayName}</span> : <RouterLink to={patientPath(row.patientId)}>{row.displayName}</RouterLink>}
          <span>({faDigits(row.fileNumber)})</span>
          {row.isHead && <Chip size="small" color="primary" label={t('pat:family_account.head')} data-testid="family-head" />}
        </Stack>
      ),
    },
    { id: 'joined', headerKey: 'pat:family_account.joined', cell: (row) => toJalali(row.joinedAt) },
    {
      id: 'actions',
      headerKey: 'pat:family_account.actions',
      cell: (row) =>
        canWrite && !row.isHead ? (
          <Stack direction="row" spacing={0.5}>
            <Button size="small" onClick={() => setHead.mutate(row.patientId)} disabled={setHead.isPending}>
              {t('pat:family_account.make_head')}
            </Button>
            <Button size="small" color="warning" onClick={() => remove.mutate(row.patientId)} disabled={remove.isPending} data-testid="family-remove">
              {t('pat:family_account.remove')}
            </Button>
          </Stack>
        ) : null,
    },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="family-account">
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h2" sx={{ flexGrow: 1 }}>
          {family.data ? t('pat:family_account.title_named', { name: family.data.name }) : t('pat:family_account.title')}
        </Typography>
        {canWrite && family.isSuccess && !family.data && (
          <Button onClick={() => setCreating(true)} data-testid="family-create">
            {t('pat:family_account.create')}
          </Button>
        )}
        {canWrite && family.data && (
          <Button color="error" onClick={() => setDissolving(true)} data-testid="family-dissolve">
            {t('pat:family_account.dissolve')}
          </Button>
        )}
      </Stack>
      {family.error ? <ErrorState error={family.error} compact onRetry={() => void family.refetch()} /> : null}
      {error ? <ErrorState error={error} compact /> : null}
      {family.isSuccess && !family.data && (
        <Typography variant="body2" color="text.secondary" data-testid="family-none">
          {t('pat:family_account.none')}
        </Typography>
      )}
      {family.data && (
        <Stack spacing={1.5}>
          <DataTable
            captionKey="pat:family_account.members"
            columns={columns}
            rows={family.data.members}
            getRowId={(row) => row.patientId}
            emptyTitleKey="pat:family_account.none"
          />
          {canWrite && (
            <PatientPicker
              labelKey="pat:family_account.add_member"
              value={picker}
              onChange={(item) => {
                setPicker(item);
                if (item) {
                  add.mutate(item.id);
                }
              }}
              excludeIds={family.data.members.map((member) => member.patientId)}
              testId="family-add-member"
            />
          )}
          <Alert severity="info" variant="outlined">
            {t('pat:family_account.rule')}
          </Alert>
        </Stack>
      )}
      {creating && <CreateFamilyDialog patient={patient} open={creating} onClose={() => setCreating(false)} />}
      <ConfirmDialog
        open={dissolving}
        titleKey="pat:family_account.dissolve"
        messageKey="pat:family_account.dissolve_confirm"
        destructive
        busy={dissolve.isPending}
        onConfirm={() => dissolve.mutate()}
        onCancel={() => setDissolving(false)}
      />
    </Paper>
  );
}
